/**
 * Годовая свёртка пятилетнего прогноза.
 *
 * Вынесено из ForecastAmazonUS: арифметика года 0 (бюджет запуска в P&L и в постоянных
 * затратах) — это числа, по которым принимают решения о закупке, и проверять их через
 * рендер компонента дороже и менее надёжно, чем прямым вызовом.
 */

/** Поля месяца прогноза, которые участвуют в годовой свёртке. */
export interface ForecastMonthlyPoint {
  revenue: number;
  /** P&L месяца. Месяц 0 уже содержит вычтенный бюджет запуска. */
  pl: number;
  cfMonthly: number;
  adSpend: number;
  monthlyProfit: number;
  profitWithoutAds: number;
  cumInvValue: number;
}

export interface ForecastYearlyRow {
  year: string;
  revenue: number;
  revenueGrowth: number;
  pl: number;
  plGrowth: number;
  cashFlow: number;
  cfGrowth: number;
  adSpend: number;
  fixedCosts: number;
  profit: number;
  profitWithoutAds: number;
  avgMargin: number;
  avgROI: number;
}

export interface YearlyForecastInput {
  /** 60 месяцев прогноза в хронологическом порядке. */
  months: ForecastMonthlyPoint[];
  /**
   * Подписи лет. Число строк задаёт длина `months` (см. `yearlyRowCount`), а не длина
   * этого массива: подписей должно хватить на все строки, иначе свёртка бросает.
   * Первый год неполный, последний — тоже, если месяцы не легли ровно по годам.
   */
  yearLabels: string[];
  monthlyExpenses: number;
  launchBudget: number;
}

/** Первый год прогноза неполный: он покрывает месяцы 0..5, дальше идут полные 12. */
const FIRST_YEAR_MONTHS = 6;
const MONTHS_IN_YEAR = 12;

/**
 * Границы года по индексам месяцев: год 0 — [0, 6), год k — [(k-1)*12+6, k*12+6).
 */
export function yearBounds(yearIndex: number): { start: number; end: number } {
  if (yearIndex === 0) return { start: 0, end: FIRST_YEAR_MONTHS };
  return {
    start: (yearIndex - 1) * MONTHS_IN_YEAR + FIRST_YEAR_MONTHS,
    end: yearIndex * MONTHS_IN_YEAR + FIRST_YEAR_MONTHS,
  };
}

/**
 * Сколько годовых строк нужно, чтобы покрыть `monthCount` месяцев целиком.
 *
 * Хвост, не добравший до полного года, получает собственную строку: 60 месяцев — это
 * неполный первый год (6) + четыре полных (48) + неполный хвост (6). Пока строк было
 * пять, последние шесть месяцев таблицы не попадали ни в одну из них, и годовые итоги
 * расходились с помесячными и с знаменателем ROI, взятым за все 60 месяцев.
 */
export function yearlyRowCount(monthCount: number): number {
  if (monthCount <= 0) return 0;
  if (monthCount <= FIRST_YEAR_MONTHS) return 1;
  return 1 + Math.ceil((monthCount - FIRST_YEAR_MONTHS) / MONTHS_IN_YEAR);
}

export function buildYearlyForecast({
  months,
  yearLabels,
  monthlyExpenses,
  launchBudget,
}: YearlyForecastInput): ForecastYearlyRow[] {
  const rowCount = yearlyRowCount(months.length);
  if (yearLabels.length < rowCount) {
    throw new Error(
      `buildYearlyForecast: ${months.length} месяцев требуют ${rowCount} подписей года, передано ${yearLabels.length}`,
    );
  }

  const rows: ForecastYearlyRow[] = [];
  // База сравнения — годовой темп: неполный год приводится к двенадцати месяцам, иначе
  // его рост против полного соседа читался бы как обвал, которого в модели нет.
  let prevRevAnnual = 0;
  let prevPLAnnual = 0;
  let prevCFAnnual = 0;

  for (let yi = 0; yi < rowCount; yi++) {
    const { start, end } = yearBounds(yi);
    const stop = Math.min(end, months.length);
    let yRev = 0, yPL = 0, yCF = 0, yAd = 0, yFC = 0, yProfit = 0, yProfitNoAds = 0;
    for (let m = start; m < stop; m++) {
      const p = months[m];
      yRev += p.revenue;
      yPL += p.pl;
      yCF += p.cfMonthly;
      yAd += p.adSpend;
      yFC += monthlyExpenses;
      yProfit += p.monthlyProfit;
      yProfitNoAds += p.profitWithoutAds;
    }
    // Бюджет запуска уже вычтен из P&L месяца 0 (ForecastAmazonUS: `if (mi === 0) pl -= LAUNCH_BUDGET`),
    // поэтому в годовой свёртке он только пополняет постоянные затраты года 0.
    if (yi === 0) { yFC += launchBudget; }

    const annualize = MONTHS_IN_YEAR / (stop - start);
    const revG = prevRevAnnual > 0 ? ((yRev * annualize - prevRevAnnual) / prevRevAnnual) * 100 : 0;
    const plG = prevPLAnnual !== 0 ? ((yPL * annualize - prevPLAnnual) / Math.abs(prevPLAnnual)) * 100 : 0;
    const cfG = prevCFAnnual !== 0 ? ((yCF * annualize - prevCFAnnual) / Math.abs(prevCFAnnual)) * 100 : 0;
    const avgM = yRev > 0 ? (yProfit / yRev) * 100 : 0;
    const lastMonth = stop - 1;
    const totalInv = months[lastMonth].cumInvValue + launchBudget;
    const avgROI = totalInv > 0 ? (yPL / totalInv) * 100 : 0;

    rows.push({
      year: yearLabels[yi], revenue: Math.round(yRev), revenueGrowth: Math.round(revG),
      pl: Math.round(yPL), plGrowth: Math.round(plG), cashFlow: Math.round(yCF), cfGrowth: Math.round(cfG),
      adSpend: Math.round(yAd), fixedCosts: Math.round(yFC), profit: Math.round(yProfit),
      profitWithoutAds: Math.round(yProfitNoAds),
      avgMargin: Math.round(avgM * 10) / 10, avgROI: Math.round(avgROI * 10) / 10,
    });

    prevRevAnnual = yRev * annualize;
    prevPLAnnual = yPL * annualize;
    prevCFAnnual = yCF * annualize;
  }

  return rows;
}
